import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRooms } from '../context/RoomContext';
import { useAuth } from '../context/AuthContext';
import { format, addDays, differenceInDays } from 'date-fns';
import { Wifi, Tv, Wind, Coffee, Users, DollarSign, Calendar, AlertCircle } from 'lucide-react';

const RoomDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getRoomById, bookRoom } = useRooms();
  const { currentUser } = useAuth();
  
  const room = getRoomById(id || '');
  
  const [checkIn, setCheckIn] = useState<string>(
    format(new Date(), 'yyyy-MM-dd')
  );
  const [checkOut, setCheckOut] = useState<string>(
    format(addDays(new Date(), 1), 'yyyy-MM-dd')
  );
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  
  if (!room) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900">Room not found</h2>
        <p className="mt-2 text-gray-600">The room you're looking for doesn't exist.</p>
        <button
          onClick={() => navigate('/rooms')}
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Back to Rooms
        </button>
      </div>
    );
  }
  
  const getAmenityIcon = (amenity: string) => {
    switch (amenity.toLowerCase()) {
      case 'wi-fi':
        return <Wifi className="h-5 w-5" />;
      case 'tv':
        return <Tv className="h-5 w-5" />;
      case 'air conditioning':
        return <Wind className="h-5 w-5" />;
      default:
        return <Coffee className="h-5 w-5" />;
    }
  };
  
  const handleBooking = () => {
    if (!currentUser) {
      navigate('/login', { state: { from: `/rooms/${id}` } });
      return;
    }
    
    const checkInDate = new Date(checkIn);
    const checkOutDate = new Date(checkOut);
    
    if (checkOutDate <= checkInDate) {
      setError('Check-out date must be after check-in date');
      return;
    }
    
    const nights = differenceInDays(checkOutDate, checkInDate);
    const totalPrice = room.price * nights;
    
    bookRoom({
      roomId: room.id,
      userId: currentUser.id,
      userName: currentUser.name,
      userEmail: currentUser.email,
      checkIn,
      checkOut,
      totalPrice,
    });
    
    setSuccess('Room booked successfully!');
    setTimeout(() => {
      navigate('/');
    }, 2000);
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="md:flex">
          <div className="md:flex-shrink-0">
            <img
              className="h-64 w-full object-cover md:w-96"
              src={room.imageUrl}
              alt={room.name}
            />
          </div>
          <div className="p-8 w-full">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{room.name}</h2>
                <p className="mt-2 text-gray-600">{room.description}</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-indigo-600">${room.price}</p>
                <p className="text-gray-500">per night</p>
              </div>
            </div>
            
            <div className="mt-6 border-t border-gray-200 pt-6">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-gray-500" />
                <span className="ml-2 text-gray-700">Capacity: {room.capacity} {room.capacity > 1 ? 'people' : 'person'}</span>
              </div>
            </div>
            
            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-900">Amenities</h3>
              <div className="mt-2 grid grid-cols-2 gap-4">
                {room.amenities.map((amenity, index) => (
                  <div key={index} className="flex items-center">
                    <div className="flex-shrink-0 text-indigo-500">
                      {getAmenityIcon(amenity)}
                    </div>
                    <p className="ml-2 text-gray-700">{amenity}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 p-8 border-t border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Book This Room</h3>
          
          {error && (
            <div className="mb-4 bg-red-50 p-4 rounded-md flex items-start">
              <AlertCircle className="h-5 w-5 text-red-400 mt-0.5" />
              <p className="ml-3 text-sm text-red-700">{error}</p>
            </div>
          )}
          
          {success && (
            <div className="mb-4 bg-green-50 p-4 rounded-md">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="check-in" className="block text-sm font-medium text-gray-700">
                Check-in Date
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="date"
                  id="check-in"
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                  value={checkIn}
                  min={format(new Date(), 'yyyy-MM-dd')}
                  onChange={(e) => {
                    setCheckIn(e.target.value);
                    setError('');
                  }}
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="check-out" className="block text-sm font-medium text-gray-700">
                Check-out Date
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="date"
                  id="check-out"
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                  value={checkOut}
                  min={format(addDays(new Date(checkIn), 1), 'yyyy-MM-dd')}
                  onChange={(e) => {
                    setCheckOut(e.target.value);
                    setError('');
                  }}
                />
              </div>
            </div>
          </div>
          
          <div className="mt-6">
            <div className="bg-gray-100 p-4 rounded-md">
              <div className="flex justify-between">
                <span className="text-gray-700">Price per night</span>
                <span className="text-gray-900">${room.price}</span>
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-gray-700">
                  Nights
                </span>
                <span className="text-gray-900">
                  {differenceInDays(new Date(checkOut), new Date(checkIn))}
                </span>
              </div>
              <div className="flex justify-between mt-2 pt-2 border-t border-gray-200">
                <span className="font-medium text-gray-900">Total</span>
                <span className="font-medium text-gray-900">
                  ${room.price * differenceInDays(new Date(checkOut), new Date(checkIn))}
                </span>
              </div>
            </div>
          </div>
          
          <div className="mt-6">
            <button
              onClick={handleBooking}
              className="w-full bg-indigo-600 border border-transparent rounded-md shadow-sm py-3 px-4 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Book Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomDetailPage;